import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card as MuiCard,
  CardMedia,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import LanguageIcon from '@mui/icons-material/Language';

interface Card {
  id: string;
  nome: string;
  cognome: string;
  club: {
    name: string;
  };
  active: boolean;
  year: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`card-tabpanel-${index}`}
      aria-labelledby={`card-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function PublicCardViewPage() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [card, setCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  const fetchCard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use axios directly without the api instance to avoid auth interceptor
      const response = await fetch(`${apiUrl}/get-card-info/${cardId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch card');
      }
      
      const data = await response.json();
      setCard(data);
    } catch (err: any) {
      console.error('Error fetching card:', err);
      setError(err.message || t('errors.failedToLoadCard'));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, cardId, t]);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'it' : 'en';
    i18n.changeLanguage(newLang);
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !card) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('errors.cardNotFound')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          variant="outlined"
        >
          {t('actions.backToHome')}
        </Button>
      </Container>
    );
  }

  const frontImageUrl = `${apiUrl}/get-image/${cardId}/front`;
  const backImageUrl = `${apiUrl}/get-image/${cardId}/back`;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Tooltip title={t('common.language')}>
            <IconButton onClick={toggleLanguage} color="primary">
              <LanguageIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography variant="h4" gutterBottom align="center">
          {t('cards.membershipCard')}
        </Typography>
        
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6">
            {card.nome || 'N/A'} {card.cognome || ''}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {card.club.name} - {card.year}
          </Typography>
          {card.active && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              {t('cards.status.active')}
            </Typography>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            <Tab label={t('cards.front')} />
            <Tab label={t('cards.back')} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <MuiCard sx={{ maxWidth: '100%', margin: 'auto' }}>
            <CardMedia
              component="img"
              image={frontImageUrl}
              alt={t('cards.frontAlt')}
              sx={{ width: '100%', height: 'auto' }}
            />
          </MuiCard>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MuiCard sx={{ maxWidth: '100%', margin: 'auto' }}>
            <CardMedia
              component="img"
              image={backImageUrl}
              alt={t('cards.backAlt')}
              sx={{ width: '100%', height: 'auto' }}
            />
          </MuiCard>
        </TabPanel>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            {t('actions.backToHome')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => window.open(`${apiUrl}/get-pdf/${cardId}`, '_blank')}
          >
            {t('actions.downloadPDF')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}